// src/features/files/utils/getFolderName.js
import i18n from 'i18next';
import { tokenService } from '../../auth/services/tokenService';

/**
 * Get the display name for a folder, handling i18n translation keys
 * @param {string} folderName - The folder name from API (may be a translation key)
 * @returns {string} The translated folder name or the raw name
 *
 * @example
 * // If folderName is "i18n-files:i18n_folder_names.tasks"
 * // Returns the translated value of "files:i18n_folder_names.tasks"
 * getFolderName("i18n-files:i18n_folder_names.tasks")
 *
 * // Regular folder names are returned as-is
 * getFolderName("My Folder") // Returns "My Folder"
 */
export function getFolderName(folderName) {
  if (!folderName) return '';

  // Type check: ensure folderName is a string
  if (typeof folderName !== 'string') {
    throw new TypeError(`getFolderName expects a string, but received ${typeof folderName}: ${JSON.stringify(folderName)}`);
  }

  // Check if this is an i18n key
  if (folderName.startsWith('i18n-files:i18n_folder_names.')) {
    // Convert "i18n-files:i18n_folder_names.tasks" to "files:i18n_folder_names.tasks"
    const translationKey = folderName.slice(5); // Remove "i18n-" prefix
    return i18n.t(translationKey);
  }

  // Return the name as-is if it's not a translation key
  return folderName;
}

const APP_FOLDER_PREFIX = 'i18n-files:i18n_folder_names.';

/**
 * Folders the user shouldn't rename, move or delete: the folders the app looks
 * up by name (Udstyr, Opgaver, Lokaler …). Renaming or moving one of those
 * would make the app create a new one next to it.
 */
export function isAppFolder(folder) {
  return Boolean(folder?.folder_name?.startsWith('i18n-files:'));
}

/**
 * Whether a file or folder belongs to the user's department. Folders and files
 * shared with all departments (is_system_folder / is_system_file) belong to the
 * department that manages them: others can open them, not change them.
 * Items without a department_id come from lists of the department's own files.
 */
export function isOwnItem(item) {
  return !item?.department_id || item.department_id === tokenService.getDepartmentId();
}

/**
 * How Orbit manages a folder, so it can be shown as such:
 * - { type: 'shared', own, department }: shared with all departments by `department`;
 *   `own` when that is the user's department, which can change it like any folder
 * - { type: 'system' }: one of Orbit's module folders (Udstyr …)
 * - { type: 'linked', kind }: the folder Orbit made for one piece of equipment,
 *   room, task or item, inside that module's folder (kind: 'equipment', 'rooms' …).
 *   It's found again by its name, so it can be deleted but not renamed or moved.
 * - null: the department's own folders.
 * `parentFolderName` is the raw name of the folder it's in.
 */
export function getFolderLink(folder, parentFolderName) {
  if (folder?.is_system_folder) {
    return { type: 'shared', own: isOwnItem(folder), department: folder.department_name };
  }
  if (isAppFolder(folder)) return { type: 'system' };
  if (parentFolderName?.startsWith(APP_FOLDER_PREFIX)) {
    return { type: 'linked', kind: parentFolderName.slice(APP_FOLDER_PREFIX.length) };
  }
  return null;
}
